<template>
  <div class="block">
    <div class="flex items-center justify-center gap-2">
      <Checkbox v-model='isRecurringTask' binary size="large" id="rec_box"
      :disabled="editable === false"/>
      <label for="rec_box" class="flex items-center justify-center cursor-pointer" id="lbl_recBox2"
      :class="{'text-[var(--color-disabled)]': editable === false}">
        {{ $t('KssRecurringTaskComponent.isRecurringTask') }}
      </label>
    </div>
    <div v-if="isRecurringTask" class="w-full py-4 px-4 my-2" id="drop_event">
      <div class="flex items-center justify-center">
        <div>
          <Select v-model="type" :options="recTaskDropdown" optionLabel="name"
            optionValue ="value" class="w-56 bg-[var(--color-light)] h-10"
            name="eventtime" id="select_event"
            :disabled="editable === false"
            :class="{'text-[var(--color-disabled)]': editable === false}"/>
        </div>
      </div>
      <div v-if="normalizedType === 'custom'" class="flex flex-wrap justify-center items-center flex-row mt-4
        gap-2" id="custom_choice">
        <label class="flex relative m-1 text-base w-fit" for="nmbr">
            {{ $t('KssRecurringTaskComponent.repeatEvery') }}
        </label>
        <InputNumber v-model="everyX" min="1" id="nmbr" inputStyle="width:50px" class="h-10"
        :readonly="editable === false"/>
        <Select v-model="subtype" :options="customDropdown" optionLabel="name"
            optionValue ="value" class="w-32 bg-[var(--color-light)] h-10"
            name="nmbrevent" id="nmbr_event" :disabled="editable === false"/>
      </div>
      <div v-if="normalizedType === 'daily' || (normalizedType === 'custom' && subtype === 'days')"
        class="flex items-center justify-center gap-2 mt-4">
        <Checkbox
          v-model="includeWeekends"
          binary
          id="include_weekends_edit"
          :disabled="editable === false"
        />
        <label
          for="include_weekends_edit"
          class="cursor-pointer text-base"
          :class="{'text-[var(--color-disabled)]': editable === false}"
        >
          {{ $t('KssRecurringTaskComponent.includeWeekends') }}
        </label>
      </div>
      <div v-if="normalizedType === 'monthly'" class="flex flex-wrap justify-center items-center flex-row mt-4
        gap-2" id="monthly_week_choice">
        <label class="flex relative m-1 text-base w-fit" for="week_of_month_edit">
          {{ $t('KssRecurringTaskComponent.weekOfMonth') }}
        </label>
        <Select
          v-model="weekOfMonth"
          :options="weekOfMonthOptions"
          optionLabel="name"
          optionValue="value"
          class="w-64 bg-[var(--color-light)] h-10"
          inputId="week_of_month_edit"
          :disabled="editable === false"
        />
      </div>
      <div v-if="showWeekDays" id="seven_days" data-testid="kss-edit-recurring-days"
        class="flex items-center justify-center flex-wrap mt-6">
        <label id="day" v-for="(day, index) in getDays" :key="day.rSchName" @click="selectDay(index)"
            class="flex relative m-1 text-sm w-fit"
            :data-testid="`kss-edit-recurring-day-${day.rSchName}`">
          <div class="flex p-3 w-full rounded-lg m-0 cursor-pointer border-[1px]
                    border-[var(--color-light-grey)]"
        :class="{'text-[var(--color-orange)] border-[var(--color-orange)] bg-[var(--color-bisque)]'
                     : day.isSelected}">
            <span :class="{'font-semibold': day.isSelected}">
              {{ $t('KssRecurringTaskComponent.daysShort.' + day.rSchName) }}</span>
          </div>
        </label>
      </div>
      <div class="flex flex-col items-center justify-center gap-2 mt-4">
        <label for="series_end_date_edit" class="text-base">
          {{ $t('KssRecurringTaskComponent.seriesEndDate') }}
        </label>
        <DatePicker
          inputId="series_end_date_edit"
          :modelValue="seriesEndDateCalendarValue"
          @update:modelValue="onSeriesEndDateCalendarChange"
          :dateFormat="PRIMEVUE_DATE_FORMAT"
                :placeholder="LOCALE_DATE_PLACEHOLDER"
          class="h-10 rounded-lg bg-[var(--color-light)]"
          :disabled="editable === false"
          showIcon
          iconDisplay="input"
        />
        <button
          v-if="seriesEndDate"
          type="button"
          class="text-sm text-[var(--color-orange)]"
          :disabled="editable === false"
          @click="clearSeriesEndDate"
        >
          {{ $t('KssRecurringTaskComponent.clearSeriesEndDate') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { Checkbox, InputNumber, Select } from 'primevue';
import DatePicker from 'primevue/datepicker';
import { v4 as uuidv4 } from 'uuid';
import {
  PRIMEVUE_DATE_FORMAT,
  LOCALE_DATE_PLACEHOLDER,
  parseIsoDateOnly,
} from '@/helpers/formatLocaleDate';

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

export default {
  props: {
    editable: {
      type: Boolean,
    },
    initialRecurring: {
      type: Object,
      default: null,
    },
    initialIncludeWeekends: {
      type: Boolean,
      default: null,
    },
    /** Meeting date used to pre-select a weekday when switching to weekly/monthly. */
    anchorDate: {
      type: Date,
      default: null,
    },
  },
  emits: ['updateReccuring'],
  name: 'EditKssRecurringTaskComponent',
  components: { Checkbox, Select, InputNumber, DatePicker },
  data() {
    return {
      id: `id_${uuidv4()}`,
      type: 'daily',
      subtype: 'days',
      everyX: 1,
      weekOfMonth: 2,
      recurringDays: [],
      isRecurringTaskData: false,
      includeWeekendsData: true,
      seriesEndDate: null,
      PRIMEVUE_DATE_FORMAT,
      LOCALE_DATE_PLACEHOLDER,
      recTaskDropdown: [
        { name: this.$t('KssRecurringTaskComponent.daily'), value: 'daily' },
        { name: this.$t('KssRecurringTaskComponent.weekly'), value: 'weekly' },
        { name: this.$t('KssRecurringTaskComponent.monthly'), value: 'monthly' },
        { name: this.$t('KssRecurringTaskComponent.custom'), value: 'custom' },
      ],
      customDropdown: [
        { name: this.$t('KssRecurringTaskComponent.days'), value: 'days' },
        { name: this.$t('KssRecurringTaskComponent.weeks'), value: 'weeks' },
      ],
      weekOfMonthOptions: [
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption1'), value: 1 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption2'), value: 2 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption3'), value: 3 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption4'), value: 4 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption5'), value: 5 },
      ],
    };
  },
  computed: {
    isRecurringTask: {
      get() {
        return this.isRecurringTaskData;
      },
      set(newValue) {
        this.isRecurringTaskData = newValue;
        if (newValue) {
          this.ensureRecurringDaysReady();
        }
        this.updateReccuringObject();
      },
    },
    normalizedType() {
      const t = this.type;
      if (t && typeof t === 'object' && t.value != null) return t.value;
      return t;
    },
    getDays() {
      return this.recurringDays;
    },
    showWeekDays() {
      const t = this.normalizedType;
      return t === 'weekly'
        || t === 'monthly'
        || (t === 'custom' && this.subtype === 'weeks');
    },
    includeWeekends: {
      get() {
        return this.includeWeekendsData;
      },
      set(value) {
        this.includeWeekendsData = value;
        this.updateReccuringObject();
      },
    },
    seriesEndDateCalendarValue() {
      if (!this.seriesEndDate) return null;
      if (this.seriesEndDate instanceof Date) return this.seriesEndDate;
      return parseIsoDateOnly(this.seriesEndDate) || new Date(this.seriesEndDate);
    },
  },
  methods: {
    selectDay(index) {
      if (this.editable === false) return;
      if (this.normalizedType === 'monthly') {
        this.recurringDays.forEach((day, i) => {
          day.isSelected = i === index;
        });
      } else {
        this.recurringDays[index].isSelected = !this.recurringDays[index].isSelected;
      }
      this.updateReccuringObject();
    },
    initRecurringDays(byDays, singleSelect = false) {
      const list = Array.isArray(byDays) ? byDays.map(String) : [];
      const effectiveDays = singleSelect && list.length > 0 ? [list[0]] : list;
      this.recurringDays = [
        { rSchName: 'MO', isSelected: effectiveDays.includes('MO') },
        { rSchName: 'TU', isSelected: effectiveDays.includes('TU') },
        { rSchName: 'WE', isSelected: effectiveDays.includes('WE') },
        { rSchName: 'TH', isSelected: effectiveDays.includes('TH') },
        { rSchName: 'FR', isSelected: effectiveDays.includes('FR') },
        { rSchName: 'SA', isSelected: effectiveDays.includes('SA') },
        { rSchName: 'SU', isSelected: effectiveDays.includes('SU') },
      ];
    },
    /** Ensure day chips exist when weekly/monthly UI is shown (non-recurring → weekly). */
    ensureRecurringDaysReady() {
      if (!Array.isArray(this.recurringDays) || this.recurringDays.length === 0) {
        this.initRecurringDays([]);
      }
      const t = this.normalizedType;
      const needsDays = t === 'weekly'
        || t === 'monthly'
        || (t === 'custom' && this.subtype === 'weeks');
      if (!needsDays) return;
      const anySelected = this.recurringDays.some((d) => d.isSelected);
      if (anySelected) return;
      const anchor = this.anchorDate instanceof Date && !Number.isNaN(this.anchorDate.getTime())
        ? this.anchorDate
        : new Date();
      const code = DAY_CODES[anchor.getDay()];
      if (t === 'monthly') {
        this.recurringDays.forEach((day) => {
          day.isSelected = day.rSchName === code;
        });
      } else {
        // Weekly / custom weeks: pre-select meeting weekday so chips are visible and valid.
        this.recurringDays.forEach((day) => {
          day.isSelected = day.rSchName === code;
        });
      }
    },
    determineType() {
      if (!this.initialRecurring) {
        return 'daily';
      }
      if (Object.prototype.hasOwnProperty.call(this.initialRecurring, 'interval')) {
        return 'custom';
      }

      if (this.initialRecurring.frequency === 'MONTHLY') {
        return 'monthly';
      }

      if (this.initialRecurring.frequency === 'WEEKLY') {
        return 'weekly';
      }

      return 'daily';
    },
    determineSubType() {
      if (this.type === 'custom') {
        if (this.initialRecurring?.frequency === 'WEEKLY') {
          return 'weeks';
        }
      }
      return 'days';
    },
    clearSeriesEndDate() {
      this.seriesEndDate = null;
      this.updateReccuringObject();
    },
    onSeriesEndDateCalendarChange(value) {
      this.seriesEndDate = value || null;
      this.updateReccuringObject();
    },
    inferIncludeWeekendsFromRule(rule) {
      if (typeof this.initialIncludeWeekends === 'boolean') {
        return this.initialIncludeWeekends;
      }
      const isDailyLike =
        this.normalizedType === 'daily'
        || (this.normalizedType === 'custom' && this.subtype === 'days');
      if (!isDailyLike) {
        return true;
      }
      const days = rule?.byDayOfWeek || [];
      if (!days.length) return true;
      return days.some((d) => d === 'SA' || d === 'SU');
    },
    parseSeriesEndDateFromRule(rule) {
      if (!rule?.end) return null;
      const d = new Date(rule.end);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    updateReccuringObject() {
      this.$emit('updateReccuring', this.getRecurringObject());
    },
    getRecurringObject() {
      const type = this.normalizedType;
      const baseMeta = {
        isReccuring: this.isRecurringTask,
        includeWeekends: this.includeWeekendsData,
        seriesEndDate: this.seriesEndDate,
      };

      switch (type) {
        case 'daily': {
          const daily = {
            ...baseMeta,
            frequency: 'DAILY',
          };
          if (!this.includeWeekendsData) {
            daily.byDayOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR'];
          }
          return daily;
        }
        case 'weekly':
          return {
            ...baseMeta,
            frequency: 'WEEKLY',
            byDayOfWeek: this.recurringDays
              .filter((e) => e.isSelected)
              .map((e) => e.rSchName),
          };
        case 'monthly': {
          const selectedDays = this.recurringDays
            .filter((e) => e.isSelected)
            .map((e) => e.rSchName);
          return {
            ...baseMeta,
            frequency: 'MONTHLY',
            byDayOfWeek: selectedDays.length ? [selectedDays[0]] : [],
            byWeekOfMonth: this.weekOfMonth,
          };
        }
        case 'custom':
          if (this.subtype === 'days') {
            const customDaily = {
              ...baseMeta,
              frequency: 'DAILY',
              interval: this.everyX,
            };
            if (!this.includeWeekendsData) {
              customDaily.byDayOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR'];
            }
            return customDaily;
          }
          if (this.subtype === 'weeks') {
            return {
              ...baseMeta,
              frequency: 'WEEKLY',
              interval: this.everyX,
              byDayOfWeek: this.recurringDays
                .filter((e) => e.isSelected)
                .map((e) => e.rSchName),
            };
          }
        // eslint-disable-next-line no-fallthrough
        default:
          return {
            ...baseMeta,
            isReccuring: false,
            frequency: 'MINUTELY',
          };
      }
    },
    applyInitialRecurringState() {
      if (this.initialRecurring && this.initialRecurring?.frequency !== 'MINUTELY') {
        this.isRecurringTaskData = true;
        this.type = this.determineType();
        this.initRecurringDays(
          this.initialRecurring?.byDayOfWeek,
          this.type === 'monthly',
        );
        this.subtype = this.determineSubType();
        this.everyX = this.initialRecurring?.interval || 1;
        this.weekOfMonth = this.initialRecurring?.byWeekOfMonth || 2;
        this.seriesEndDate = this.parseSeriesEndDateFromRule(this.initialRecurring);
        this.includeWeekendsData = this.inferIncludeWeekendsFromRule(this.initialRecurring);
        this.ensureRecurringDaysReady();
      } else {
        this.isRecurringTaskData = false;
        this.type = 'daily';
        this.subtype = 'days';
        this.everyX = 1;
        this.weekOfMonth = 2;
        this.seriesEndDate = null;
        this.initRecurringDays([]);
      }
    },
  },
  watch: {
    type() {
      if (typeof this.type === 'object' && this.type?.value != null) {
        this.type = this.type.value;
        return;
      }
      this.ensureRecurringDaysReady();
      this.updateReccuringObject();
    },
    subtype() {
      this.ensureRecurringDaysReady();
      this.updateReccuringObject();
    },
    everyX() {
      this.updateReccuringObject();
    },
    weekOfMonth() {
      this.updateReccuringObject();
    },
    includeWeekendsData() {
      this.updateReccuringObject();
    },
    seriesEndDate() {
      this.updateReccuringObject();
    },
    initialRecurring: {
      deep: true,
      handler() {
        this.applyInitialRecurringState();
        this.$nextTick(() => this.updateReccuringObject());
      },
    },
  },
  mounted() {
    this.applyInitialRecurringState();
    this.$nextTick(() => {
      this.updateReccuringObject();
    });
  },
};
</script>
